import React, { useState } from 'react';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import { Translate, useTranslate } from '../../../core/translate';
import { combineClassNames } from '../../../core/css';
import { SvgIcon } from '../../../core/icons';
import {
  ButtonText,
  ThemedButton,
  Button,
} from '../../../core/controls/button/Button';

export const onCloseModal = (onClose, ms = 500) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      onClose && onClose();
      resolve(true);
    }, ms);
  });
};

export const ModalContent = ({ setIsClosing, children }) => {
  return (
    <div className="modal">
      {React.Children.map(children, (child) => {
        return React.cloneElement(child, {
          setIsClosing,
        });
      })}
    </div>
  );
};
// TODO: ModalContent is leftover from removed setInterval code. Some of it might not be necessary.

export const ModalHeader = ({
  icon,
  text,
  analyticsKey,
  hideClose,
  headerRef,
  className,
  onClose,
  setIsClosing,
  msrp,
  contentUnderHeader,
  useLargeIcon = true,
}) => {
  const translate = useTranslate();

  const closeButton = hideClose ? null : (
    <button
      onClick={() => setIsClosing(true, onCloseModal(onClose))}
      className="modal-header__close stat-button-close"
      data-dtm={analyticsKey || text}
      aria-label={translate('byo.page.general.close')}>
      <SvgIcon
        name="close"
        className={`${useLargeIcon ? 'modal-header__close-icon--large' : ''}`}
      />
    </button>
  );
  return (
    <div>
      <h1
        className={combineClassNames(['modal-header', className])}
        ref={headerRef}>
        {icon ? (
          <SvgIcon name={icon} className="modal-header__title-icon" />
        ) : null}
        <div
          className="modal-header__title"
          dangerouslySetInnerHTML={{ __html: text }}
        />
        {closeButton}
        {msrp ? <div className="modal-header__msrp">{msrp}</div> : null}
      </h1>
      {contentUnderHeader}
    </div>
  );
};

export const ModalBody = ({
  children,
  bodyRef,
  className,
  innerClassName,
  setIsClosing: _isClosing,
  ...rest
}) => {
  return (
    <div className={`modal-body ${className || ''}`} {...rest}>
      <div
        className={`modal-body__inner ${innerClassName || ''}`}
        ref={bodyRef}>
        {children}
      </div>
    </div>
  );
};

export const PEModalBody = ({
  children,
  bodyRef,
  className,
  innerClassName,
  setIsClosing: _isClosing,
  ...rest
}) => {
  return (
    <div className={`pe-modal-body ${className || ''}`} {...rest}>
      <div
        className={`modal-body__inner ${innerClassName || ''}`}
        ref={bodyRef}>
        {children}
      </div>
    </div>
  );
};

export const ModalFooterBase = ({ setIsClosing, children, className }) => {
  return (
    <div className={combineClassNames(['modal-footer', className])}>
      {React.Children.map(children, (child) => {
        return React.cloneElement(child, {
          setIsClosing,
        });
      })}
    </div>
  );
};

export const ModalFooter = ({
  setIsClosing,
  children,
  footerRef,
  className,
  innerClassName,
}) => (
  <div
    className={combineClassNames(['modal-footer', className])}
    ref={footerRef}>
    <div
      className={combineClassNames(
        'modal-footer__inner',
        innerClassName ?? innerClassName
      )}>
      {React.Children.map(children, (child) => {
        return React.cloneElement(child, {
          setIsClosing,
        });
      })}
    </div>
  </div>
);

export const ModalFooterMessage = ({ children, className }) => (
  <div className={combineClassNames('modal-footer__message', className)}>
    {children}
  </div>
);

export const ModalFooterButtons = ({
  setIsClosing,
  children,
  classModifier,
}) => (
  <div
    className={combineClassNames(
      'modal-footer__buttons',
      'modal-footer__buttons--inline',
      classModifier ? `modal-footer__buttons--${classModifier}` : ''
    )}>
    {React.Children.map(children, (child) => {
      return React.cloneElement(child, {
        setIsClosing,
      });
    })}
  </div>
);

export const ModalButtonPair = ({ setIsClosing, children }) => (
  <>
    {React.Children.map(children, (child) => {
      return React.cloneElement(child, {
        setIsClosing,
      });
    })}
  </>
);

export const ModalCustomContent = ({ setIsClosing, children }) => {
  return (
    <>
      {children({
        setIsClosing,
        onCloseModal,
      })}
    </>
  );
};

export const ModalFooterCancelButton = (props) => {
  const { setIsClosing, className, onClose, ...rest } = props;
  return (
    <ThemedButton
      {...rest}
      onClick={() =>
        setIsClosing ? setIsClosing(true, onCloseModal(onClose)) : onClose()
      }
      analytics="text"
      className={className}>
      <ButtonText>
        <Translate value="byo.page.general.cancel" />
      </ButtonText>
    </ThemedButton>
  );
};

ModalFooterCancelButton.defaultProps = {
  theme: {
    fallback: 'button--secondary',
    buick: 'button--secondary',
    cadillac: 'button--secondary',
    chevrolet: 'button--secondary--invert',
    gmc: 'button--secondary--invert',
    fleet: 'button--secondary',
  },
};

export const ModalFooterPrimaryButton = (props) => {
  const { setIsClosing, className, children, onSelect, ...rest } = props;
  return (
    <ThemedButton
      {...rest}
      theme={{
        fallback: 'button--primary',
        buick: 'button--primary',
        cadillac: 'button--primary',
        chevrolet: 'button--primary--invert',
        gmc: 'button--primary--invert',
        fleet: 'button--primary',
      }}
      onClick={() =>
        setIsClosing ? setIsClosing(true, onCloseModal(onSelect)) : onSelect()
      }
      analytics="text"
      className={className}>
      <ButtonText>
        {children ? children : <Translate value="byo.page.general.ok" />}
      </ButtonText>
    </ThemedButton>
  );
};

export const ModalFooterCustomButton = (props) => {
  const { setIsClosing, className, children, onSelect, ...rest } = props;
  return (
    <Button
      {...rest}
      onClick={() =>
        setIsClosing ? setIsClosing(true, onCloseModal(onSelect)) : onSelect()
      }
      analytics="text"
      className={className}>
      <ButtonText>
        {children ? children : <Translate value="byo.page.general.ok" />}
      </ButtonText>
    </Button>
  );
};

export const ModalSpan = ({
  children,
  className,
  setIsClosing,
  onClose,
  analyticsKey,
}) => {
  return (
    <button
      onClick={() => setIsClosing(true, onCloseModal(onClose))}
      className={combineClassNames(['modal-span', className])}
      data-dtm={analyticsKey}>
      {children}
    </button>
  );
};

export const Modal = ({
  children,
  isOpen,
  contentLabel,
  className,
  bodyOpenClassName,
  onRequestClose,
  fullscreen = true,
  overlayClassName,
  contentRefCallback = () => null,
}) => {
  const [isClosing, setIsClosing] = useState(false);

  return (
    <ReactModal
      isOpen={isOpen}
      className={`reveal ${className} ${
        fullscreen ? 'reveal--fullscreen' : ''
      } ${isClosing ? 'close-modal' : ''}`}
      overlayClassName={combineClassNames(['reveal-overlay', overlayClassName])}
      contentLabel={contentLabel}
      onRequestClose={() => setIsClosing(true, onCloseModal(onRequestClose))}
      appElement={document.getElementById('byo-web-ui')}
      contentRef={contentRefCallback}
      bodyOpenClassName={bodyOpenClassName}>
      <ModalContent isClosing={isClosing} setIsClosing={setIsClosing}>
        {children}
      </ModalContent>
      {/* TODO: ModalContent is leftover from removed setInterval code. Some of it might not be necessary. */}
    </ReactModal>
  );
};

Modal.defaultProps = {
  isOpen: false,
  className: '',
};

Modal.propTypes = {
  isOpen: PropTypes.bool,
  contentLabel: PropTypes.string,
  className: PropTypes.string,
  onRequestClose: PropTypes.func.isRequired,
};

export default Modal;
